import {
  faArrowLeft,
  faCircleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import { Button } from '@e2n/ui';
import { SeitenHinweis } from './Hinweis.tsx';
import { EndpunktListe } from './EndpunktListe.tsx';
import { DatenabrufKarte } from './DatenabrufKarte.tsx';
import type { DatenabrufEintrag } from './DatenabrufListe.tsx';
import { ZugangDrawer } from './ZugangDrawer.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type {
  EndpunktDefinitionInput,
  KatalogEndpunkt,
} from '../gql/graphql.ts';
import {
  type KatalogAuswahl,
  useKatalogAuswahl,
} from '../hooks/useKatalogAuswahl.ts';
import { useIsMobile } from '../hooks/useIsMobile.ts';
import { useTranslation } from 'react-i18next';
import { useZugangTitelVergeben } from '../hooks/useZugangTitelVergeben.ts';
import { GET_KATALOG } from '../lib/graphql/queries.ts';
import { useQuery } from '@apollo/client/react';
import { useNavigate } from 'react-router-dom';

type ZugangFormularProps = {
  titel: string;
  zugangUid?: string;
  startAuswahl: KatalogAuswahl;
  startTitel: string;
  laeuft: boolean;
  absendenText: string;
  onAbsenden: (titel: string, definitionen: EndpunktDefinitionInput[]) => void;
};

export function ZugangFormular(props: ZugangFormularProps) {
  const { t } = useTranslation();
  const { data, loading, error } = useQuery(GET_KATALOG);

  if (loading) {
    return null;
  }

  if (error || !data) {
    return (
      <SeitenHinweis
        icon={faCircleExclamation}
        art="fehler"
        titel={t('domain.e2nApi.fehler.laden.titel')}
        beschreibung={t('domain.e2nApi.fehler.laden.beschreibung')}
      />
    );
  }

  return <ZugangFormularInhalt {...props} endpunkte={data.katalog} />;
}

type ZugangFormularInhaltProps = ZugangFormularProps & {
  endpunkte: KatalogEndpunkt[];
};

function ZugangFormularInhalt({
  titel,
  zugangUid,
  startAuswahl,
  startTitel,
  laeuft,
  absendenText,
  onAbsenden,
  endpunkte,
}: ZugangFormularInhaltProps) {
  const navigate = useNavigate();
  const istMobil = useIsMobile();

  const {
    katalogAuswahl,
    toggleEndpunkt,
    toggleAttribut,
    toggleAlleAttribute,
  } = useKatalogAuswahl(bereinigeAuswahl(startAuswahl, endpunkte));
  const gewaehlteEndpunkte = filterGewaehlteEndpunkte(
    endpunkte,
    katalogAuswahl,
  );

  const vergebeneTitel = useZugangTitelVergeben(zugangUid);

  function handleAbsenden(titel: string) {
    onAbsenden(titel, mapToInputs(gewaehlteEndpunkte, katalogAuswahl));
  }

  if (istMobil) {
    return (
      <ZugangDrawer
        titel={titel}
        endpunkte={endpunkte}
        katalogAuswahl={katalogAuswahl}
        datenabruf={mapToDatenabruf(gewaehlteEndpunkte, katalogAuswahl)}
        startTitel={startTitel}
        vergebeneTitel={vergebeneTitel}
        absendenText={absendenText}
        laeuft={laeuft}
        onEndpunktToggle={toggleEndpunkt}
        onAttributToggle={toggleAttribut}
        onAlleAttributeToggle={toggleAlleAttribute}
        onAbsenden={handleAbsenden}
        onSchliessen={() => navigate('..')}
      />
    );
  }

  return (
    <ZugangFormularDesktop
      titel={titel}
      endpunkte={endpunkte}
      katalogAuswahl={katalogAuswahl}
      datenabruf={mapToDatenabruf(gewaehlteEndpunkte, katalogAuswahl)}
      startTitel={startTitel}
      vergebeneTitel={vergebeneTitel}
      absendenText={absendenText}
      laeuft={laeuft}
      onEndpunktToggle={toggleEndpunkt}
      onAttributToggle={toggleAttribut}
      onAbsenden={handleAbsenden}
      onZurueck={() => navigate('..')}
    />
  );
}

type ZugangFormularDesktopProps = {
  titel: string;
  endpunkte: KatalogEndpunkt[];
  katalogAuswahl: KatalogAuswahl;
  datenabruf: DatenabrufEintrag[];
  startTitel: string;
  vergebeneTitel: string[];
  absendenText: string;
  laeuft: boolean;
  onEndpunktToggle: (endpunkt: KatalogEndpunkt) => void;
  onAttributToggle: (endpunktName: string, attributName: string) => void;
  onAbsenden: (titel: string) => void;
  onZurueck: () => void;
};

function ZugangFormularDesktop({
  titel,
  endpunkte,
  katalogAuswahl,
  datenabruf,
  startTitel,
  vergebeneTitel,
  absendenText,
  laeuft,
  onEndpunktToggle,
  onAttributToggle,
  onAbsenden,
  onZurueck,
}: ZugangFormularDesktopProps) {
  const { t } = useTranslation();

  return (
    <div className="grid min-h-full lg:grid-cols-2">
      <div className="bg-white p-4 md:p-10">
        <div className="flex items-center gap-1">
          <Button
            variant="icon"
            size="icon"
            onClick={onZurueck}
            aria-label={t('domain.e2nApi.neuerZugang.zurueck')}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </Button>
          <h1 className="text-2xl font-extrabold text-neutrals-900">{titel}</h1>
        </div>
        <h2 className="mt-8 text-base font-bold text-neutrals-900">
          {t('domain.e2nApi.neuerZugang.subtitle')}
        </h2>

        <EndpunktListe
          endpunkte={endpunkte}
          gewaehlteEndpunkte={katalogAuswahl}
          onEndpunktToggle={onEndpunktToggle}
          onAttributToggle={onAttributToggle}
        />
      </div>

      <div className="p-4 md:p-12">
        <DatenabrufKarte
          absendenText={absendenText}
          datenabruf={datenabruf}
          startTitel={startTitel}
          vergebeneTitel={vergebeneTitel}
          auswahlVorhanden={datenabruf.length > 0}
          laeuft={laeuft}
          onAbsenden={onAbsenden}
        />
      </div>
    </div>
  );
}

function filterGewaehlteEndpunkte(
  endpunkte: KatalogEndpunkt[],
  auswahl: KatalogAuswahl,
): KatalogEndpunkt[] {
  return endpunkte.filter((endpunkt) => endpunkt.name in auswahl);
}

function bereinigeAuswahl(
  auswahl: KatalogAuswahl,
  endpunkte: KatalogEndpunkt[],
): KatalogAuswahl {
  return Object.fromEntries(
    endpunkte
      .filter((endpunkt) => endpunkt.name in auswahl)
      .map((endpunkt) => [
        endpunkt.name,
        auswahl[endpunkt.name].filter((attributName) =>
          endpunkt.attribute.some((attribut) => attribut.name === attributName),
        ),
      ]),
  );
}

function mapToDatenabruf(
  endpunkte: KatalogEndpunkt[],
  auswahl: KatalogAuswahl,
): DatenabrufEintrag[] {
  return endpunkte.map((endpunkt) => ({
    name: endpunkt.name,
    anzeigename: endpunkt.anzeigename,
    gewaehlt: auswahl[endpunkt.name].length,
    gesamt: endpunkt.attribute.length,
  }));
}

function mapToInputs(
  endpunkte: KatalogEndpunkt[],
  auswahl: KatalogAuswahl,
): EndpunktDefinitionInput[] {
  return endpunkte.map((endpunkt) => ({
    endpunktName: endpunkt.name,
    httpMethode: endpunkt.httpMethode,
    attribute: auswahl[endpunkt.name],
  }));
}
